/* eslint-disable react/forbid-prop-types */
import React, { isValidElement, useLayoutEffect, useMemo, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import i18n from 'simple-react-i18n'
import { Paper, TableContainer, TableHead, TableRow, Table, TablePagination, TableCell, TableBody,
    Icon, Card, CardContent, Tooltip, IconButton, TableSortLabel, Box, Select, MenuItem } from '@mui/material'
import { isFunction, isNil, isNumber, isObject, isString,
    isUndefined, orderBy, partition, sum, toString } from 'lodash'
import { getI18nOrLabel } from 'utils/StringUtil'
import { nbPerPageLabelShort } from 'referencial/constants/ReferencialConstants'
import useUpdateEffect from 'utils/customHook/useUpdateEffect'
import { styled } from '@mui/material/styles'
import { CardTitle } from 'components/card/NewCard'
import { NoDataPanel } from 'components/card/NoDataCard'
import { isDate, getTimestampFromString } from 'utils/DateUtil'
import { KeyboardArrowDown, UnfoldMore } from '@mui/icons-material'
import { getTextColorByBackground } from 'utils/ColorUtil'
import { useLocation } from 'react-router'
import { getStorageValue, setStorageValue } from 'utils/customHook/useLocalStorage'

// https://virtuoso.dev/mui-table-virtual-scroll/

const SORT = {
    ASC: 'asc',
    DESC: 'desc',
}

const ALIGN = {
    LEFT: 'left',
    RIGHT: 'right',
    CENTER: 'center',
    TOP: 'top',
    BOTTOM: 'bottom',
    SPACE_BETWEEN: 'space-between',
}

const isDefined = v => !((isNumber(v) && isNaN(v)) || isNil(v) || v === '') || v === 0
const sortRows = (rows, sort = {}) => {
    const { column, direction } = sort
    if (isUndefined(column) || isUndefined(direction)) return rows

    const [withValue, withoutValue] = partition(rows, elem => {
        if (isValidElement(elem[column])) return false
        if (!isObject(elem[column])) return isDefined(elem[column])
        const {
            value,
            sortValue = value,
        } = elem[column]
        if (isValidElement(sortValue)) return false
        return isDefined(sortValue)
    })

    const getSortValue = elem => {
        let v = isObject(elem[column]) ? elem[column]?.sortValue ?? elem[column]?.value : elem[column]
        if (isString(v) && isDate(v)) {
            return getTimestampFromString(v)
        }
        return v
    }

    const orderedRows = orderBy(withValue, getSortValue, direction)
    return [...orderedRows, ...withoutValue]
}

const WrapperBox = styled(Box)({
    padding: '0px 10px',
})

const WrapperPaper = styled(Paper)({
    padding: '0px 10px',
})

const WrapperCardContent = styled(CardContent)({
    width: '100%',
    overflow: 'hidden',
    padding: '0px 10px !important', // just to override the theme
})

const WrapperAccordionDetails = styled(Paper)({
    padding: '0px 10px',
})

const NewTable = ({
    rows = [],
    headers = [],
    headersLabel = {}, // deprecated
    lineActions = [],

    onClickRow,
    defaultSort = {},
    onSort,
    rowsPerPageOptions = nbPerPageLabelShort,

    maxHeight,
    displayHeaders = true,
    WrapperComponent = WrapperPaper,
    inverseDisplay = false,
    noDataDisplay,
    'data-cy': dataCy = 'table',
    onMouseEnter,
    onMouseLeave,

    id,
    disableLocalStorage = false,
}) => {
    // sort and pagination attribution from localStorage
    const location = useLocation()
    const tableKey = window.btoa(encodeURIComponent(`${location.pathname}-${toString(headers)}${id ? `-${id}` : ''}`))
    const minRowsPerPage = rowsPerPageOptions[0]?.value ?? rowsPerPageOptions[0]

    const tableContainerRef = useRef(null)
    const isInDialogRef = useRef(false)

    useLayoutEffect(() => {
        // Go threw all parents elements of the NewTable component and return either the first parent element that satisfies the condition or nothing
        isInDialogRef.current = !!tableContainerRef.current?.closest('.MuiDialog-root, [role="dialog"]')
    }, [])

    const [initialPaginationAndSort] = useState(() => {
        const fallback = { sort: defaultSort, page: 0, rowsPerPage: minRowsPerPage }
        if (disableLocalStorage) return fallback
        return getStorageValue(tableKey) ?? fallback
    })

    const [sort, setSort] = useState(initialPaginationAndSort.sort)

    useUpdateEffect(() => {
        onSort?.(sort)
    }, [sort])

    const sortedRows = useMemo(() => sortRows(rows, sort), [rows, sort])

    const [page, setPage] = useState(initialPaginationAndSort.page)
    const [rowsPerPage, setRowsPerPage] = useState(initialPaginationAndSort.rowsPerPage)

    // La page peut être hors bornes si le jeu de lignes rétrécit (filtre/recherche) alors qu'une
    // page > 0 était mémorisée -> tableau vide. On borne à la dernière page valide.
    const lastPage = (minRowsPerPage > 0 && rowsPerPage > 0) ? Math.max(0, Math.ceil(sortedRows.length / rowsPerPage) - 1) : 0
    const safePage = Math.min(page, lastPage)
    useUpdateEffect(() => {
        if (page !== safePage) {
            setPage(safePage)
        }
    }, [safePage])

    useUpdateEffect(() => {
        if (disableLocalStorage || isInDialogRef.current || rowsPerPage === -1) return
        setStorageValue(tableKey, { sort, page, rowsPerPage })
    }, [sort, page, rowsPerPage])

    const filteredRows = minRowsPerPage > 0 && rowsPerPage !== -1 ? sortedRows.slice(
        safePage * rowsPerPage,
        safePage * rowsPerPage + rowsPerPage,
    ) : sortedRows

    const inverseDisplayRows = inverseDisplay ? filteredRows.toReversed() : filteredRows

    const filteredLineActions = lineActions.filter(a => a.displayed || isUndefined(a.displayed))
    const [rightActions, leftActions] = partition(filteredLineActions, a => a.align === ALIGN.RIGHT)

    const offsetLeftAction = leftActions.length * 23
    const offsetRightAction = rightActions.length * 23

    const listLeftSize = headers.map(h => h.sticky === ALIGN.LEFT && h.width || 0)
    const listRightSize = headers.map(h => h.sticky === ALIGN.RIGHT && h.width || 0).toReversed()

    const offsetLeftStickyHeaders = listLeftSize.reduce((acc, width, i) => {
        acc.push(offsetLeftAction + sum(listLeftSize.slice(0, i)) + width)
        return acc
    }, [offsetLeftAction])

    const offsetRightStickyHeaders = listRightSize.reduce((acc, width, i) => {
        acc.push(offsetRightAction + sum(listRightSize.slice(0, i)) + width)
        return acc
    }, [offsetRightAction]).toReversed()

    return (
        <WrapperComponent>
            <TableContainer
                ref={tableContainerRef}
                sx={{
                    maxHeight,
                    overflowY: maxHeight ? 'auto' : 'hidden',

                    // Ne pas toucher !! Obligatoire pour fixer la taille du tableau en cas d'un grand nombre de colonnes. Voir avec Thomas
                    width: 0,
                    minWidth: '100%',
                    overflowX: 'auto',
                }}
            >
                <Table stickyHeader aria-label='sticky table' size='small' sx={{ ['& tr:last-child td']: { borderBottom: minRowsPerPage < rows.length ? undefined : 'none' } }}>
                    <TableHead sx={displayHeaders ? {} : { display: 'none' }}>
                        <TableRow>
                            {leftActions.map((a, i) => (<TableCell key={lineActions.indexOf(a)} variant='headerIcon' sx={{ left: `${i * 23}px` }} />))}
                            {headers.map((header, i) => {
                                const {
                                    key,
                                    value,
                                    onClick,
                                    sticky,
                                    width,
                                    color,
                                    tooltip,
                                    isRequired,
                                    noSort,
                                    paddingBottom,
                                } = isObject(header) ? header : { key: header }
                                const isNullValue = key.startsWith('nullValue')
                                const isSorted = sort.column === key
                                const coloredHeaderStyle = color ? {
                                    paddingRight: i === headers.length - 1 ? 0 : 0.5,
                                    paddingLeft: i === 0 ? 0 : 0.5,
                                } : {}
                                const tableCell = (
                                    <TableCell
                                        variant='head'
                                        sx={{
                                            ...coloredHeaderStyle,
                                            paddingBottom,
                                            minWidth: width,
                                            width,
                                            ...(!isNil(sticky) ? {
                                                position: 'sticky',
                                                zIndex: 3,
                                                [sticky]: `${sticky === ALIGN.LEFT ? offsetLeftStickyHeaders[i] : offsetRightStickyHeaders[i + 1]}px`,
                                            } : {}),
                                        }}
                                        key={i}
                                        sortDirection={isSorted ? sort?.direction : false}
                                    >
                                        {!noSort && (isValidElement(value) || (!isNullValue && !isValidElement(value))) ?
                                            <TableSortLabel
                                                active={isSorted}
                                                direction={isSorted ? sort?.direction : undefined}
                                                onClick={onClick ?? (() => {
                                                    setSort(({ direction, column }) => {
                                                        if (column !== key) return { direction: SORT.ASC, column: key }
                                                        if (direction === SORT.ASC) return { direction: SORT.DESC, column: key }
                                                        return {} // direction === SORT.DESC
                                                    })
                                                })}
                                                IconComponent={isSorted ? KeyboardArrowDown : UnfoldMore}
                                                slotProps={{
                                                    icon: {
                                                        sx: { color: `${getTextColorByBackground(color || 'white')} !important` },
                                                    },
                                                }}
                                                sx={{ bgcolor: color, borderRadius: 1, padding: color ? '0.6rem 1rem' : '0.5rem 1.5rem 0.5rem 0.3rem ' }}
                                            >
                                                {isValidElement(value) && value}
                                                {(!isNullValue && !isValidElement(value) && value) ?? headersLabel[key] ?? getI18nOrLabel(key)}
                                                {isRequired && '*'}
                                            </TableSortLabel>
                                            :
                                            <>
                                                {isValidElement(value) && value}
                                                {(!isNullValue && !isValidElement(value) && value) ?? headersLabel[key] ?? getI18nOrLabel(key)}
                                                {isRequired && '*'}
                                            </>
                                        }
                                    </TableCell>
                                )
                                if (tooltip) {
                                    return (
                                        <Tooltip key={i} title={tooltip}>
                                            {tableCell}
                                        </Tooltip>
                                    )
                                }
                                return tableCell
                            })}
                            {rightActions.map((a, i) => (<TableCell key={lineActions.indexOf(a)} variant='headerIcon' sx={{ left: `${i * 23}px` }} />))}
                        </TableRow>
                    </TableHead>
                    <TableBody data-cy={`${dataCy}_body`}>
                        {
                            inverseDisplayRows.map((row) => {
                                const ir = rows.indexOf(row)
                                const funcOnClickRow = onClickRow && (() => onClickRow(row, ir))

                                return (
                                    <TableRow
                                        key={ir}
                                        sx={{ backgroundColor: row.colorLine ?? 'unset' }}
                                    >
                                        {leftActions.map((a, i) => {
                                            const icon = (row.lineAction || isNil(row.lineAction)) && (
                                                <IconButton
                                                    sx={{ color: a.color ?? '#000' }}
                                                    size='actionTable'
                                                    onClick={() => a.onClick?.(row, ir)}
                                                >
                                                    <Icon fontSize='inherit'>{isFunction(a.icon) ? a.icon(row) : a.icon}</Icon>
                                                </IconButton>
                                            )
                                            return (
                                                <TableCell
                                                    key={lineActions.indexOf(a)}
                                                    variant='bodyIcon'
                                                    align={ALIGN.CENTER}
                                                    sx={{ left: `${i * 23}px` }}
                                                >
                                                    {a.tooltip ? (
                                                        <Tooltip key={i} title={isFunction(a.tooltip) ? a.tooltip(row) : a.tooltip}>
                                                            {icon}
                                                        </Tooltip>
                                                    ) : icon}
                                                </TableCell>
                                            )
                                        })}
                                        {headers.map((header, i) => {
                                            const {
                                                key,
                                                width,
                                                sticky,
                                            } = isObject(header) ? header : { key: header }

                                            if (!isObject(row[key]) || isValidElement(row[key])) {
                                                return (
                                                    <TableCell
                                                        key={i}
                                                        variant='body'
                                                        sx={{
                                                            backgroundColor: row.colorLine ?? 'white',
                                                            cursor: isUndefined(funcOnClickRow) ? 'default' : 'pointer',

                                                            color: 'black',
                                                            maxWidth: width,
                                                            ...(!isNil(sticky) ? {
                                                                position: 'sticky',
                                                                [sticky]: `${sticky === ALIGN.LEFT ? offsetLeftStickyHeaders[i] : offsetRightStickyHeaders[i + 1]}px`,
                                                            } : {}),
                                                        }}
                                                        onClick={funcOnClickRow}
                                                        padding={isNil(row[key]) ? 'none' : 'normal'}
                                                        onMouseEnter={() => onMouseEnter?.(row)}
                                                        onMouseLeave={() => onMouseLeave?.(row)}
                                                    >
                                                        {row[key]}
                                                    </TableCell>
                                                )
                                            }
                                            const {
                                                value,
                                                tooltip,
                                                align = ALIGN.LEFT,
                                                verticalAlign,
                                                color = row.colorLine ?? 'white',
                                                fontColor = 'black', // getTextColorByHexColor(color)
                                                onClick: onClickCell = funcOnClickRow,
                                                noPadding,

                                                rightIcon = {},
                                                leftIcon = {},
                                            } = row[key] ?? {}

                                            const {
                                                icon: leftIconName,
                                                color: leftIconColor = 'grey',
                                                tooltip: leftIconTooltip,
                                            } = leftIcon

                                            const {
                                                icon: rightIconName,
                                                color: rightIconColor = 'grey',
                                                tooltip: rightIconTooltip,
                                            } = rightIcon

                                            // une couleur posee sur la cellule prime sur le gris de survol de la ligne (theme MuiTableRow)
                                            const keepColorOnHover = isNil(row[key]?.color) ? {} : { '&&&': { backgroundColor: row[key].color } }

                                            const tableCell = (
                                                <TableCell
                                                    key={i}
                                                    variant='body'
                                                    sx={{
                                                        backgroundColor: color,
                                                        ...keepColorOnHover,
                                                        cursor: isUndefined(onClickCell) ? 'default' : 'pointer',

                                                        color: fontColor,
                                                        maxWidth: width,
                                                        ...(!isNil(sticky) ? {
                                                            position: 'sticky',
                                                            [sticky]: `${sticky === ALIGN.LEFT ? offsetLeftStickyHeaders[i] : offsetRightStickyHeaders[i + 1]}px`,
                                                        } : {}),
                                                        verticalAlign,
                                                    }}
                                                    onClick={onClickCell}
                                                    padding={noPadding || (isNil(value) && isNil(leftIconName) && isNil(rightIconName)) ? 'none' : 'normal'}
                                                >
                                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: align, '& > div:first-child': { width: '100%' } }}>
                                                        {!isUndefined(leftIconName) && !isUndefined(leftIconTooltip) && (
                                                            <Tooltip title={leftIconTooltip}><Icon sx={{ color: leftIconColor, marginRight: '5px' }}>{leftIconName}</Icon></Tooltip>
                                                        )}
                                                        {!isUndefined(leftIconName) && isUndefined(leftIconTooltip) && (
                                                            <Icon sx={{ color: leftIconColor, marginRight: '5px' }}>{leftIconName}</Icon>
                                                        )}
                                                        {value ?? ''}
                                                        {!isUndefined(rightIconName) && !isUndefined(rightIconTooltip) && (
                                                            <Tooltip title={rightIconTooltip}><Icon sx={{ color: rightIconColor, marginLeft: '5px' }}>{rightIconName}</Icon></Tooltip>
                                                        )}
                                                        {!isUndefined(rightIconName) && isUndefined(rightIconTooltip) && (
                                                            <Icon sx={{ color: rightIconColor, marginLeft: '5px' }}>{rightIconName}</Icon>
                                                        )}
                                                    </Box>
                                                </TableCell>
                                            )
                                            if (tooltip) {
                                                return (
                                                    <Tooltip key={i} title={isFunction(tooltip) ? tooltip(row) : tooltip}>
                                                        {tableCell}
                                                    </Tooltip>
                                                )
                                            }
                                            return tableCell
                                        })}
                                        {rightActions.map((a, i) => {
                                            const icon = (row.lineAction || isNil(row.lineAction)) && (
                                                <IconButton
                                                    sx={{ color: a.color ?? '#000' }}
                                                    size='actionTable'
                                                    onClick={() => a.onClick?.(row, ir)}
                                                >
                                                    <Icon fontSize='inherit'>{isFunction(a.icon) ? a.icon(row) : a.icon}</Icon>
                                                </IconButton>
                                            )
                                            return (
                                                <TableCell
                                                    key={lineActions.indexOf(a)}
                                                    variant='bodyIcon'
                                                    align={ALIGN.CENTER}
                                                    sx={{ left: `${i * 23}px` }}
                                                >
                                                    {a.tooltip ? (
                                                        <Tooltip key={i} title={isFunction(a.tooltip) ? a.tooltip(row) : a.tooltip}>
                                                            {icon}
                                                        </Tooltip>
                                                    ) : icon}
                                                </TableCell>
                                            )
                                        })}
                                    </TableRow>
                                )
                            })
                        }
                    </TableBody>
                </Table>
            </TableContainer>
            {minRowsPerPage < rows.length && (
                <TablePagination
                    rowsPerPageOptions={rowsPerPageOptions}
                    component='div'
                    count={rows.length}
                    rowsPerPage={rowsPerPage}
                    page={safePage}
                    onPageChange={(_, p) => setPage(p)}
                    onRowsPerPageChange={(e) => {
                        setRowsPerPage(+e.target.value)
                        setPage(0)
                    }}
                    showFirstButton
                    showLastButton
                />
            )}
            {
                !rows.length && (noDataDisplay || (<NoDataPanel />))
            }
        </WrapperComponent>
    )
}

NewTable.propTypes = {
    rows: PropTypes.arrayOf(PropTypes.object).isRequired,
    // rows: PropTypes.arrayOf(PropTypes.objectOf(PropTypes.oneOfType([
    //     PropTypes.number,
    //     PropTypes.string,
    //     PropTypes.element,
    //     PropTypes.shape({
    //         value: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.element]),
    //         sortValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    //         onClick: PropTypes.func,
    //         color: PropTypes.string,
    //         fontColor: PropTypes.string,
    //         tooltip: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
    //         align: PropTypes.oneOf([ALIGN.LEFT, ALIGN.RIGHT, ALIGN.CENTER]),
    //         noPadding: PropTypes.bool,
    //         leftIcon: PropTypes.shape({
    //             icon: PropTypes.string,
    //             color: PropTypes.string,
    //         }),
    //         rightIcon: PropTypes.shape({
    //             icon: PropTypes.string,
    //             color: PropTypes.string,
    //         }),
    //     }),
    // ]))).isRequired,
    headers: PropTypes.arrayOf(PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.shape({
            key: PropTypes.string.isRequired,
            value: PropTypes.oneOfType([
                PropTypes.string,
                PropTypes.element,
            ]),
            sticky: PropTypes.oneOf([ALIGN.LEFT, ALIGN.RIGHT]),
            width: PropTypes.number,
            maxWidth: PropTypes.oneOfType([
                PropTypes.number,
                PropTypes.string,
            ]),
            color: PropTypes.string,
            paddingBottom: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
            tooltip: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
            noSort: PropTypes.bool,
        }),
    ])).isRequired,
    /**
     * @deprecated use property value on headers instead
     */
    headersLabel: PropTypes.objectOf(PropTypes.oneOfType([PropTypes.string, PropTypes.element])),
    lineActions: PropTypes.arrayOf(PropTypes.shape({
        icon: PropTypes.oneOfType([PropTypes.string, PropTypes.func]).isRequired,
        onClick: PropTypes.func.isRequired,
        align: PropTypes.oneOf([ALIGN.LEFT, ALIGN.RIGHT]),
        displayed: PropTypes.bool,
        color: PropTypes.string,
        tooltip: PropTypes.string,
    })),

    onClickRow: PropTypes.func,
    defaultSort: PropTypes.shape({
        column: PropTypes.string.isRequired, // header
        direction: PropTypes.oneOf([SORT.ASC, SORT.DESC]).isRequired,
    }),
    onSort: PropTypes.func,
    rowsPerPageOptions: PropTypes.arrayOf(PropTypes.shape({
        value: PropTypes.number.isRequired,
        label: PropTypes.string.isRequired,
    })),

    maxHeight: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    displayHeaders: PropTypes.bool,
    WrapperComponent: PropTypes.elementType,
    inverseDisplay: PropTypes.bool,
    noDataDisplay: PropTypes.element,
    onMouseEnter: PropTypes.func,
    onMouseLeave: PropTypes.func,
    'data-cy': PropTypes.string,
    id: PropTypes.string,
    disableLocalStorage: PropTypes.bool,
}

const CardTable = ({
    title = '',
    subTitle = '',
    subtitleSize,
    actions = [],
    hideNbElements = false,
    color = '#FFF',

    rows = [],
    headers = [],
    headersLabel = {}, // deprecated
    lineActions = [],

    onClickRow,
    defaultSort,
    onSort,
    rowsPerPageOptions,

    maxHeight,
    displayHeaders = true,
    inverseDisplay = false,
    noDataDisplay,
    'data-cy': dataCy,
    id,
    disableLocalStorage = false,
    centerTitle = false,
}) => {
    const formattedTitle = useMemo(() => {
        if (hideNbElements) {
            return title
        }
        const nbElements = rows.length
        return (
            <>
                {isValidElement(title) ? title : <span style={{ fontSize: '1.4rem', fontWeight: 'bold' }}>{title}</span>}
                <span style={{ fontSize: '1.4rem', paddingLeft: '5px' }}>({nbElements} {nbElements > 1 ? i18n.elements : i18n.element})</span>
            </>
        )
    }, [hideNbElements, rows.length, title])

    return (
        <Card>
            <CardTitle
                title={formattedTitle}
                centerTitle={centerTitle}
                subTitle={subTitle}
                actions={actions}
                color={color}
                subtitleSize={subtitleSize}
            />
            <NewTable
                rows={rows}
                headers={headers}
                headersLabel={headersLabel}
                lineActions={lineActions}

                onClickRow={onClickRow}
                defaultSort={defaultSort}
                onSort={onSort}
                rowsPerPageOptions={rowsPerPageOptions}

                maxHeight={maxHeight}
                displayHeaders={displayHeaders}
                WrapperComponent={WrapperCardContent}
                data-cy={dataCy}

                inverseDisplay={inverseDisplay}
                noDataDisplay={noDataDisplay}

                id={id}
                disableLocalStorage={disableLocalStorage}
            />
        </Card>
    )
}

CardTable.propTypes = {
    title: PropTypes.oneOfType([PropTypes.string, PropTypes.element]).isRequired,
    subTitle: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
    subtitleSize: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    actions: PropTypes.arrayOf(PropTypes.oneOfType([
        PropTypes.element,
        PropTypes.shape({
            icon: PropTypes.string,
            tooltip: PropTypes.oneOfType([
                PropTypes.string,
                PropTypes.element,
            ]),
            onClick: PropTypes.func,
            color: PropTypes.string,
            displayed: PropTypes.bool,
            'data-cy': PropTypes.string,
        }),
    ])),
    hideNbElements: PropTypes.bool,
    color: PropTypes.string,

    rows: PropTypes.arrayOf(PropTypes.object).isRequired,
    // rows: PropTypes.arrayOf(PropTypes.objectOf(PropTypes.oneOfType([
    //     PropTypes.number,
    //     PropTypes.string,
    //     PropTypes.element,
    //     PropTypes.shape({
    //         value: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.element]),
    //         sortValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    //         onClick: PropTypes.func,
    //         color: PropTypes.string,
    //         fontColor: PropTypes.string,
    //         tooltip: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
    //         align: PropTypes.oneOf([ALIGN.LEFT, ALIGN.RIGHT, ALIGN.CENTER]),
    //         noPadding: PropTypes.bool,
    //         leftIcon: PropTypes.shape({
    //             icon: PropTypes.string,
    //             color: PropTypes.string,
    //         }),
    //         rightIcon: PropTypes.shape({
    //             icon: PropTypes.string,
    //             color: PropTypes.string,
    //         }),
    //     }),
    // ]))).isRequired,
    headers: PropTypes.arrayOf(PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.shape({
            key: PropTypes.string.isRequired,
            value: PropTypes.oneOfType([
                PropTypes.string,
                PropTypes.element,
            ]),
            sticky: PropTypes.oneOf([ALIGN.LEFT, ALIGN.RIGHT]),
            width: PropTypes.number,
            maxWidth: PropTypes.oneOfType([
                PropTypes.number,
                PropTypes.string,
            ]),
            color: PropTypes.string,
            paddingBottom: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
            tooltip: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
            noSort: PropTypes.bool,
        }),
    ])).isRequired,
    /**
     * @deprecated use property value on headers instead
     */
    headersLabel: PropTypes.objectOf(PropTypes.oneOfType([PropTypes.string, PropTypes.element])),
    lineActions: PropTypes.arrayOf(PropTypes.shape({
        icon: PropTypes.oneOfType([PropTypes.string, PropTypes.func]).isRequired,
        onClick: PropTypes.func.isRequired,
        align: PropTypes.oneOf([ALIGN.LEFT, ALIGN.RIGHT]),
        displayed: PropTypes.bool,
        color: PropTypes.string,
        tooltip: PropTypes.string,
    })),

    onClickRow: PropTypes.func,
    defaultSort: PropTypes.shape({
        column: PropTypes.string.isRequired, // header
        direction: PropTypes.oneOf([SORT.ASC, SORT.DESC]).isRequired,
    }),
    onSort: PropTypes.func,
    rowsPerPageOptions: PropTypes.arrayOf(PropTypes.shape({
        value: PropTypes.number.isRequired,
        label: PropTypes.string.isRequired,
    })),

    maxHeight: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    displayHeaders: PropTypes.bool,
    inverseDisplay: PropTypes.bool,
    noDataDisplay: PropTypes.element,
    'data-cy': PropTypes.string,
    id: PropTypes.string,
    disableLocalStorage: PropTypes.bool,
    centerTitle: PropTypes.bool,
}

const TableSelect = ({
    value = '',
    items = [],
    onChange = () => {},
    keyValue = 'id',
    keyLabel = 'label',
    width = '300px',
}) => {
    return (
        <Select
            value={value}
            onChange={e => {
                e.stopPropagation()
                onChange(e.target.value !== '' ? e.target.value : undefined)
            }}
            onClick={e => e.stopPropagation()}
            blurOnSelect
            disableUnderline
            style={{ width, height: '24px', backgroundColor: 'white', border: '1px solid #666' }}
            sx={{ '.MuiOutlinedInput-notchedOutline': { borderStyle: 'none' } }}
        >
            <MenuItem sx={{ height: 'calc(1.5rem + 12px)' }} value={''}>{''}</MenuItem>
            {items.map((obj, i) => (<MenuItem key={i} value={obj[keyValue]}>{obj[keyLabel]}</MenuItem>))}
        </Select>
    )
}

TableSelect.propTypes = {
    value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    items: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        label: PropTypes.label,
    })),
    onChange: PropTypes.func,
    keyValue: PropTypes.string,
    keyLabel: PropTypes.string,
    width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
}

export {
    NewTable,
    CardTable,
    TableSelect,
    WrapperBox,
    WrapperPaper,
    WrapperCardContent,
    WrapperAccordionDetails,
    sortRows,
    SORT,
    ALIGN,
}